<template>
    <v-app class="landing-app">
        <v-layout>
            <v-main>
                <slot />
            </v-main>
        </v-layout>
    </v-app>
</template>

<script lang="ts" setup>
    import { onMounted, onUnmounted } from 'vue';

    onMounted(() => {
        document.documentElement.classList.add('has-landing');
        document.documentElement.classList.remove('has-marketing');
        document.documentElement.classList.remove('has-glass');
        document.documentElement.classList.remove('has-glass-light');
        document.documentElement.classList.remove('has-glass-dark');
    });

    onUnmounted(() => {
        document.documentElement.classList.remove('has-landing');
        delete document.documentElement.dataset.landingTheme;
    });
</script>

<style lang="scss">
    html.has-landing {
        --l2-bg: #0f0a19;
        --l2-bg-elevated: #1a1225;
        --l2-bg-card: rgba(26, 18, 37, 0.7);
        --l2-surface: #251a33;
        --l2-border: rgba(167, 139, 250, 0.25);
        --l2-border-subtle: rgba(255, 255, 255, 0.08);
        --l2-text: #faf5ff;
        --l2-text-muted: #c4b5d4;
        --l2-text-subtle: #8b7a9e;
        --l2-accent: #a78bfa;
        --l2-accent-secondary: #67e8f9;
        --l2-accent-soft: rgba(167, 139, 250, 0.15);
        --l2-accent-glow: rgba(167, 139, 250, 0.4);
        --l2-accent-secondary-soft: rgba(103, 232, 249, 0.12);
        --l2-accent-secondary-glow: rgba(103, 232, 249, 0.28);
        --l2-highlight: #e9d5ff;
        --l2-radius: 20px;
        --l2-radius-sm: 12px;
        --l2-transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        --l2-nav-bg: rgba(15, 10, 25, 0.85);
        --l2-lightbox-bg: var(--l2-bg-elevated);
        --l2-expand-hint-bg: rgba(0, 0, 0, 0.7);
        --l2-expand-hint-text: var(--l2-text);
        --l2-expand-hint-icon: var(--l2-accent);
        --l2-card-muted-bg: rgba(15, 10, 25, 0.5);
        --l2-card-glass-bg: rgba(15, 10, 25, 0.8);
        --l2-dialog-bg: var(--l2-bg-elevated);
        --l2-dialog-border: rgba(167, 139, 250, 0.25);
        --l2-grain-opacity: 0.03;

        color-scheme: dark;
        background: var(--l2-bg);
        scroll-behavior: smooth;
    }

    html.has-landing[data-landing-theme='light'] {
        --l2-bg: #f8fafc;
        --l2-bg-elevated: #ffffff;
        --l2-bg-card: rgba(255, 255, 255, 0.75);
        --l2-surface: #f1f5f9;
        --l2-border: rgba(99, 102, 241, 0.28);
        --l2-border-subtle: rgba(15, 23, 42, 0.1);
        --l2-text: #0f172a;
        --l2-text-muted: #334155;
        --l2-text-subtle: #64748b;
        --l2-accent: #7c3aed;
        --l2-accent-secondary: #0891b2;
        --l2-accent-soft: rgba(124, 58, 237, 0.12);
        --l2-accent-glow: rgba(124, 58, 237, 0.25);
        --l2-accent-secondary-soft: rgba(8, 145, 178, 0.12);
        --l2-accent-secondary-glow: rgba(8, 145, 178, 0.2);
        --l2-highlight: #4c1d95;
        --l2-nav-bg: rgba(248, 250, 252, 0.85);
        --l2-lightbox-bg: var(--l2-bg-elevated);
        --l2-expand-hint-bg: rgba(15, 23, 42, 0.7);
        --l2-expand-hint-text: #f8fafc;
        --l2-expand-hint-icon: var(--l2-expand-hint-text);
        --l2-card-muted-bg: rgba(255, 255, 255, 0.65);
        --l2-card-glass-bg: rgba(255, 255, 255, 0.8);
        --l2-dialog-bg: var(--l2-bg-elevated);
        --l2-dialog-border: rgba(99, 102, 241, 0.18);
        --l2-grain-opacity: 0.05;

        color-scheme: light;
    }

    @supports (scrollbar-gutter: stable) {
        .landing-app .v-main__scroller {
            scrollbar-gutter: stable;
            scroll-behavior: smooth;
        }
    }

    @supports not (scrollbar-gutter: stable) {
        .landing-app .v-main__scroller {
            overflow-y: scroll;
            scroll-behavior: smooth;
        }
    }

    html.has-landing body {
        background: var(--l2-bg);
    }

    .landing-app {
        background: var(--l2-bg) !important;
    }

    .landing-app .v-main {
        background: transparent;
    }

    html.has-landing .landing-dialog-card {
        background: var(--l2-dialog-bg) !important;
        color: var(--l2-text) !important;
        border: 1px solid var(--l2-dialog-border) !important;

        .v-card-title,
        .v-card-text,
        .v-card-actions {
            color: inherit !important;
        }

        .text-medium-emphasis {
            color: var(--l2-text-muted) !important;
            opacity: 1 !important;
        }

        .text-disabled {
            color: var(--l2-text-subtle) !important;
            opacity: 1 !important;
        }
    }

    html.has-landing[data-landing-theme='light'] .landing-dialog-card {
        // Intentionally left blank; keep selector for backward compatibility.
    }
</style>
